import { EstimateAPI } from '@business/api/estimate';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  EstimateGroupLineItemData,
  EstimateItemFormData,
  EstimateItemGroupData,
} from '@modules/organizations/OrganizationPage/Components/Design/EstimateForm';
import {
  CompactTMEstimateResponse,
  EstimateSearchParams,
  TMEstimateGroupId,
  TMEstimateId,
  TMEstimateLineItemId,
  TMEstimateResponse,
} from '@business/entities/tm';
import { AddEstimateFormData } from '@modules/organizations/OrganizationPage/Components/Design/AddEstimateModal/AddEstimateModal';
import { OrgId } from '@business/entities/organization';
import { R } from '@core/utils/r';
import { CategoryOptionId } from '@business/entities/category_option';
import { ServiceProviderId } from '@business/entities/tm/TmServiceProviderResponse';

export class EstimateBloc extends BaseBloc {
  private readonly estimateRepo = new Repository<TMEstimateResponse | CompactTMEstimateResponse>({
    getItemId: item => item.id,
  });

  readonly estimates$ = this.estimateRepo.items$.pipe(map(items => R.sortBy(items, s => R.toNumber(s.id) * -1)));

  readonly selectEstimate = this.estimateRepo.selectItem;

  onReset() {
    this.estimateRepo.reset();
  }

  fetchEstimates = (orgId?: OrgId, isCompact?: boolean) => {
    return EstimateAPI.fetchEstimates(orgId, isCompact).pipe(
      this.estimateRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_estimate_error') }),
    );
  };

  fetchEstimatesPaginated = (query?: EstimateSearchParams) => {
    return EstimateAPI.fetchEstimatesPaginated(query);
  };

  fetchEstimate = (id: TMEstimateId) => {
    return EstimateAPI.fetchEstimate(id).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_estimate_error') }),
    );
  };

  createEstimate = (data: AddEstimateFormData) => {
    return EstimateAPI.createEstimate(data).pipe(
      this.estimateRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_estimate'),
      }),
    );
  };

  recalculateEstimate = (id: TMEstimateId) => {
    return EstimateAPI.recalculateEstimate(id).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('recalculate_estimate'),
      }),
    );
  };

  updateEstimate = (id: TMEstimateId, data: EstimateItemFormData) => {
    return EstimateAPI.updateEstimate(id, data).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_estimate'),
      }),
    );
  };

  deleteEstimate = (id: TMEstimateId) => {
    return showDeleteConfirmation('Delete Estimate', `Do you really want to remove this category?`).pipe(
      switchMap(() => EstimateAPI.deleteEstimate(id)),
      this.estimateRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_category'),
      }),
    );
  };

  addOrUpdateGroup = (estimateId: TMEstimateId, data: EstimateItemGroupData) => {
    return EstimateAPI.addOrUpdateGroup(estimateId, data).pipe(this.estimateRepo.ops.upsertOne(item => ({ item })));
  };

  updateOption = (groupId: TMEstimateGroupId, optionId: CategoryOptionId, providerId: ServiceProviderId) => {
    return EstimateAPI.updateOption(groupId, optionId, providerId).pipe(
      this.estimateRepo.ops.upsertOne(item => ({ item })),
      // handleMessage({
      //   type: requestMessage('update_estimate_option'),
      // }),
    );
  };

  updateNote = (itemId: TMEstimateLineItemId, note: string) => {
    return EstimateAPI.updateNote(itemId, note);
  };

  updateWeProvide = (estimateId: TMEstimateId, groupId: TMEstimateGroupId, item: EstimateGroupLineItemData) => {
    return (
      EstimateAPI.updateWeProvide(groupId, item)
        // .pipe(this.estimateRepo.ops.upsertOne(item => ({ item })))
        .pipe(
          tap(newItem => {
            const estimate = this.estimateRepo.getItem(estimateId) as TMEstimateResponse;
            if (!estimate) return;
            const group = estimate.groups.find(g => g.id === groupId);
            if (!group) return;
            const newItems = group.items.map(i => (i.id === newItem.id ? newItem : i));
            // check if the item is new
            if (!group.items.find(i => i.id === newItem.id)) {
              newItems.push(newItem);
            }
            this.estimateRepo.updateOne({
              id: estimateId,
              changes: () => ({
                groups: estimate.groups.map(g => (g.id === groupId ? { ...g, items: newItems } : g)),
              }),
            });
          }),
        )
    );
  };

  qtyUpdate = (estimateId: TMEstimateId, groupId: TMEstimateGroupId, item: EstimateGroupLineItemData) => {
    return EstimateAPI.qtyUpdate(groupId, item).pipe(this.estimateRepo.ops.upsertOne(item => ({ item })));
    // .pipe(
    //   tap(newItems => {
    //     if (item.isCustom) {
    //       const estimate = this.estimateRepo.getItem(estimateId) as TMEstimateResponse;
    //       if (!estimate) return;
    //       const group = estimate.groups.find(g => g.id === groupId);
    //       if (!group) return;
    //       this.estimateRepo.updateOne({
    //         id: estimateId,
    //         changes: () => ({
    //           groups: estimate.groups.map(g => (g.id === groupId ? { ...g, items: newItems } : g)),
    //         }),
    //       });
    //     } else {
    //       this.estimateRepo.upsertOne({ item: newItems });
    //     }
    //   }),
    // )
  };
}
